/**
 * Subdomain utility functions for global routing
 *
 * The subdomain represents a unique "slug" that can be used to identify
 * any entity in the system (projects, organizations, etc.)
 *
 * Supports subdomain-based routing like:
 * - slug.localhost:3000 (local development)
 * - slug.mindmixer-qa.com (QA environment)
 * - slug.mindmixer.com (production)
 *
 * When a subdomain is detected, the application routes to a custom view
 * based on the slug. The slug can represent projects, surveys, or any
 * other entity that needs a public-facing URL.
 */

/**
 * Reserved subdomain constant for the authenticated app interface
 */
export const APP_SUBDOMAIN = import.meta.env.VITE_APP_SUBDOMAIN || 'app';

/**
 * Extracts the subdomain (slug) from the current hostname
 * Returns null if no subdomain or if on base domain
 *
 * This is a GLOBAL function - the slug can represent any entity.
 * The application determines what the slug represents based on
 * API lookup or route matching.
 *
 * Example:
 * - "community-healthcare.localhost" → "community-healthcare"
 */
export function getSubdomain(): string | null {
  const hostname = window.location.hostname;

  // Development: localhost or 127.0.0.1
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  // Split hostname into parts
  const parts = hostname.split('.');

  // Need at least 3 parts for a subdomain (subdomain.domain.tld)
  // For localhost: subdomain.localhost (2 parts)
  if (parts.length < 2) {
    return null;
  }

  // Check if we're on localhost subdomain (e.g., projectname.localhost)
  if (parts[parts.length - 1] === 'localhost') {
    // subdomain.localhost
    if (parts.length === 2) {
      const subdomain = parts[0];
      return subdomain === 'www' ? null : subdomain;
    }
    return null;
  }

  // Check if we're on local.test subdomain (e.g., subdomain.local.test)
  // Note: This requires 3+ parts, so 'local.test' (2 parts) falls through to the check below
  if (
    parts.length >= 3 &&
    parts[parts.length - 2] === 'local' &&
    parts[parts.length - 1] === 'test'
  ) {
    // subdomain.local.test (e.g., app.local.test, org.local.test)
    const subdomain = parts[0];
    return subdomain === 'www' ? null : subdomain;
  }

  // Handle base local.test domain without subdomain (2 parts only)
  if (parts.length === 2 && parts[0] === 'local' && parts[1] === 'test') {
    return null;
  }

  // Production/QA domains
  // Expected: subdomain.mindmixer-qa.com or subdomain.mindmixer.com
  if (parts.length >= 3) {
    const subdomain = parts[0];

    // Ignore www subdomain
    if (subdomain === 'www') {
      return null;
    }

    // Ignore if subdomain is the base domain name
    // (e.g., "mindmixer" in mindmixer.com or "mindmixer-qa" in mindmixer-qa.com)
    const baseDomain = parts[parts.length - 2];
    if (subdomain === baseDomain || subdomain === baseDomain.split('-')[0]) {
      return null;
    }

    return subdomain;
  }

  return null;
}

/**
 * Checks if the current request is on a subdomain
 * This indicates the app should route to subdomain-based view
 */
export function isSubdomainView(): boolean {
  return getSubdomain() !== null;
}

/**
 * Gets the slug from the current subdomain
 * Returns null if not on a subdomain
 *
 * The slug can represent any entity (project, organization, etc.)
 * Deprecated aliases for backward compatibility
 */
export function getSlugFromSubdomain(): string | null {
  return getSubdomain();
}

// Backward compatibility aliases
export const isProjectSubdomainView = isSubdomainView;
export const getProjectSlugFromSubdomain = getSlugFromSubdomain;

/**
 * Converts any name to a URL-safe slug
 * - Lowercase
 * - Replace spaces and special characters with hyphens
 * - Remove consecutive hyphens
 * - Trim hyphens from start/end
 *
 * Examples:
 * - "Springfield Public School District" → "springfield-public-school-district"
 * - "Springfield Board of Education" → "springfield-board-of-education"
 * - "My Survey 2024!" → "my-survey-2024"
 */
export function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Trim hyphens from start and end
}

// Backward compatibility alias
export const projectNameToSlug = nameToSlug;

/**
 * Checks if the current hostname is the app subdomain
 * The app subdomain hosts the authenticated admin interface
 *
 * Examples:
 * - app.localhost → true
 * - localhost → false
 * - org.localhost → false
 */
export function isAppSubdomain(): boolean {
  const subdomain = getSubdomain();
  return subdomain === APP_SUBDOMAIN;
}

/**
 * Checks if the current hostname is an organization subdomain
 * Returns true if subdomain exists AND is not 'app' or 'www'
 *
 * Note: This only checks the pattern - backend validates if org actually exists
 *
 * Examples:
 * - org.localhost → true
 * - app.localhost → false
 * - www.localhost → false
 * - localhost → false
 */
export function isOrgSubdomain(): boolean {
  const subdomain = getSubdomain();
  if (!subdomain) {
    return false;
  }
  // Exclude 'app' and 'www' - these are reserved
  // Note: Backend handles full reserved subdomain validation
  return subdomain !== APP_SUBDOMAIN && subdomain !== 'www';
}

/**
 * Extracts organization slug from the current subdomain
 * Returns null if not on an org subdomain (base domain, app subdomain, or www)
 *
 * Note: Does not validate if org actually exists - backend handles that
 *
 * Examples:
 * - org.localhost → "org"
 * - elite-capital-partners.domain.com → "elite-capital-partners"
 * - app.localhost → null
 * - www.domain.com → null
 * - localhost → null
 */
export function getOrgSlugFromSubdomain(): string | null {
  if (!isOrgSubdomain()) {
    return null;
  }
  return getSubdomain();
}

/**
 * Builds a URL for the app subdomain with the given path
 * The app subdomain hosts the authenticated admin interface
 *
 * Examples:
 * - buildAppUrl('/projects') → "http://app.localhost:3000/projects" (on localhost)
 * - buildAppUrl('projects') → "https://app.domain.com/projects" (on QA)
 * - buildAppUrl('/surveys/edit/123') → "https://app.domain.com/surveys/edit/123" (prod)
 */
export function buildAppUrl(path: string): string {
  const { hostname, port, protocol } = window.location;

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Handle localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const portSuffix = port ? `:${port}` : '';
    return `${protocol}//${APP_SUBDOMAIN}.localhost${portSuffix}${normalizedPath}`;
  }

  // Handle local.test
  if (hostname.endsWith('.local.test') || hostname === 'local.test') {
    const portSuffix = port ? `:${port}` : '';
    return `${protocol}//${APP_SUBDOMAIN}.local.test${portSuffix}${normalizedPath}`;
  }

  // Production/QA domains
  const parts = hostname.split('.');

  // Build base domain (remove any existing subdomain)
  let baseParts: string[];
  if (parts.length >= 3 && parts[0] !== 'www') {
    // Already on a subdomain - use the base domain
    baseParts = parts.slice(1);
  } else if (parts[0] === 'www') {
    // On www subdomain - use base domain
    baseParts = parts.slice(1);
  } else {
    // On base domain
    baseParts = parts;
  }

  return `${protocol}//${APP_SUBDOMAIN}.${baseParts.join('.')}${normalizedPath}`;
}

/**
 * Builds a subdomain URL for the given slug with optional path
 * Uses the current domain (localhost, qa, production)
 *
 * The slug can represent any entity (project, organization, survey, etc.)
 *
 * Examples:
 * - buildSubdomainUrl('org-slug') → "http://org-slug.localhost:3000"
 * - buildSubdomainUrl('org-slug', '/survey/123') → "http://org-slug.localhost:3000/survey/123"
 * - buildSubdomainUrl('org-slug', 'project/456') → "https://org-slug.domain.com/project/456"
 */
export function buildSubdomainUrl(slug: string, path?: string): string {
  const { hostname, port, protocol } = window.location;

  // Normalize path if provided
  const normalizedPath = path ? (path.startsWith('/') ? path : `/${path}`) : '';

  // Handle localhost (including subdomains like app.localhost)
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.localhost')
  ) {
    const portSuffix = port ? `:${port}` : '';
    return `${protocol}//${slug}.localhost${portSuffix}${normalizedPath}`;
  }

  // Handle local.test (including subdomains like app.local.test)
  if (hostname.endsWith('.local.test') || hostname === 'local.test') {
    const portSuffix = port ? `:${port}` : '';
    return `${protocol}//${slug}.local.test${portSuffix}${normalizedPath}`;
  }

  // Production/QA domains - extract base domain (last 2 parts)
  const parts = hostname.split('.');

  // Always use the last 2 parts as the base domain, regardless of current subdomain
  const baseDomain = parts.slice(-2).join('.');

  return `${protocol}//${slug}.${baseDomain}${normalizedPath}`;
}

/**
 * Builds a URL for the marketing site
 * Defaults to domain.com
 *
 * Examples:
 * - buildMarketingUrl() → "https://domain.com"
 */
export function buildMarketingUrl(): string {
  const marketingUrl = import.meta.env.VITE_MARKETING_URL || 'mindmixer.com';

  if (
    marketingUrl.startsWith('http://') ||
    marketingUrl.startsWith('https://')
  ) {
    return marketingUrl;
  }

  return `https://${marketingUrl}`;
}

// Backward compatibility alias
export const buildProjectSubdomainUrl = buildSubdomainUrl;
